import { CookieConsentState } from "./cookieConsent";

/**
 * Conditional loading of third-party tags.
 *
 * These tags used to live as inline <script> blocks in public/index.html, so they
 * fired on first paint for every visitor, before any consent decision existed.
 * They have been removed from that file and are injected here instead, and ONLY
 * after a matching grant.
 *
 * This is loaded-after-consent, not loaded-then-disabled. A "consent mode" flag
 * on an already-loaded tag still means the script was fetched, the vendor saw the
 * request, and the vendor set what it wanted to set. The spec is explicit that
 * zero requests may occur before a decision, which only conditional injection
 * achieves.
 *
 * Injection is idempotent -- re-applying the same state does not double-load --
 * and it is one-way within a page. Turning a category OFF removes its cookies and
 * stops further calls, but a script already evaluated cannot be unloaded from a
 * live document, so the page is reloaded to guarantee it is gone. That reload is
 * the only honest way to make "granted -> denied" actually take effect.
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
    fbq?: ((...args: unknown[]) => void) & { queue?: unknown[]; loaded?: boolean; version?: string; callMethod?: unknown; push?: unknown };
    _fbq?: unknown;
    __zsGatedScripts?: Record<string, boolean>;
  }
}

/** Google Ads conversion tag. */
const GTAG_ID = "AW-11222954712";

/**
 * The three Meta Pixel ids that were hardcoded in index.html.
 *
 * OWNERSHIP IS UNRESOLVED. Three distinct pixels on one site usually means three
 * parties -- an in-house account plus agencies or affiliate partners. They are
 * listed here rather than deleted so nothing is silently lost, but they stay
 * DISABLED until each one has a named owner. Removing a partner's pixel without
 * notice can breach an affiliate agreement; firing an unowned one is an
 * unexplained third-party data flow.
 */
const META_PIXEL_IDS: string[] = [
  // "1428955641002651",
  // "848269960104741",
  // "734337638762849",
];

const loaded = (): Record<string, boolean> => {
  if (!window.__zsGatedScripts) window.__zsGatedScripts = {};
  return window.__zsGatedScripts;
};

const injectScript = (key: string, src: string, onLoad?: () => void) => {
  if (loaded()[key]) return;
  loaded()[key] = true;

  const script = document.createElement("script");
  script.async = true;
  script.src = src;
  if (onLoad) script.onload = onLoad;
  document.head.appendChild(script);
};

/** Google Ads / gtag. Analytics AND marketing both have to be granted. */
const loadGoogleTag = () => {
  if (loaded().gtag) return;

  injectScript("gtag", `https://www.googletagmanager.com/gtag/js?id=${GTAG_ID}`, () => {
    window.dataLayer = window.dataLayer || [];
    // eslint-disable-next-line prefer-rest-params
    function gtag(...args: unknown[]) {
      window.dataLayer?.push(args);
    }
    gtag("js", new Date());
    gtag("config", GTAG_ID);
  });
};

const loadMetaPixels = () => {
  if (loaded().meta || META_PIXEL_IDS.length === 0) return;
  loaded().meta = true;

  injectScript("meta-base", "https://connect.facebook.net/en_US/fbevents.js", () => {
    for (const id of META_PIXEL_IDS) {
      window.fbq?.("init", id);
      window.fbq?.("track", "PageView");
    }
  });
};

/**
 * Applies a consent state by loading exactly what it permits.
 *
 * Called once on load with the stored decision, and again whenever the user
 * changes it. Loads only; never unloads — see `needsReloadToRevoke`.
 */
export const applyConsentToScripts = (state: CookieConsentState) => {
  if (typeof document === "undefined") return;

  // The Google Ads tag is a conversion/advertising tag, not plain analytics, so
  // it needs the marketing grant too. Treating it as analytics-only would let a
  // visitor who accepted analytics get an advertising tag they did not accept.
  if (state.analytics && state.marketing) {
    loadGoogleTag();
  }

  if (state.marketing) {
    loadMetaPixels();
  }

  // `functional` currently gates no third-party script. The functional cookie
  // (`zslang`, the language preference) is set by i18next in-process, so there is
  // nothing to inject -- see gateFunctionalStorage.
};

/**
 * Whether moving from `previous` to `next` requires a page reload to take effect.
 *
 * Revoking a category cannot unload a script that has already run. Rather than
 * pretend otherwise, the caller reloads. This returns true only when something
 * was actually loaded and is now withdrawn, so an ordinary first-time decision
 * never triggers a reload.
 */
export const needsReloadToRevoke = (
  previous: CookieConsentState | null,
  next: CookieConsentState
): boolean => {
  if (!previous) return false;

  const wasLoaded = (s: CookieConsentState) => ({
    gtag: s.analytics && s.marketing,
    meta: s.marketing && META_PIXEL_IDS.length > 0,
  });

  const before = wasLoaded(previous);
  const after = wasLoaded(next);

  return (before.gtag && !after.gtag) || (before.meta && !after.meta);
};

/**
 * Whether i18next may persist the language preference.
 *
 * `zslang` is a functional cookie, so writing it needs the functional grant. The
 * detector is configured with `caches: ["cookie", "localStorage"]`; this lets the
 * app consult consent before allowing that write, so language detection still
 * works in-memory for a visitor who refused functional cookies -- it just stops
 * being remembered between visits.
 */
export const functionalStorageAllowed = (state: CookieConsentState): boolean => state.functional;
